import Swal from 'sweetalert2';
import TaskTrackingService, { taskErrorMessage } from '../../services/TaskTrackingService';
import { withAccessConfirmation } from './confirmAccess';
import { contentFieldsFrom } from './taskFields';
import { LANGUAGE_NAMES, languageKey } from './taskUi';
import { refreshTaskSummary } from './useTaskSummary';

/** Task content types for the Files browser's file types. */
export const CONTENT_TYPE_FOR = { video: 'VIDEO', text: 'TEXT', book: 'BOOK' };

/** The start-bulk request body for some files and the TaskFieldsSection values. */
export const bulkBody = (items, fields, extra = {}) => ({
    items,
    languages: fields.languages,
    projectManager: fields.projectManager || undefined,
    watchers: fields.watchers,
    targetDate: fields.targetDate || undefined,
    contentFields: contentFieldsFrom(fields),
    stageOverrides: Object.fromEntries(
        Object.entries(fields.stageOverrides || {}).filter(([, o]) => o && (o.plannedAssignee || o.dueDate)),
    ),
    ...extra,
});

/** The New task request body for a title and the TaskFieldsSection values. */
export const planBody = (contentType, title, fields, extra = {}) => {
    const { items, ...body } = bulkBody([], fields, extra);
    return { ...body, contentType, title: title.trim() };
};

/** Language names a file from the Files browser already has, in the editors' spelling. */
const fileLanguageNames = (file) => {
    if (file.type === 'video') return (file.languages || []).map((l) => l?.name);
    if (file.type === 'text') return Object.keys(file.translations || {});
    if (file.type === 'book') return Object.keys(file.targetLanguageStatus || {});
    return [];
};

/**
 * The languages present in any of the selected files, as picker names in picker order.
 * VO and DubSync tracks count as their base language; names the picker doesn't know are dropped.
 */
export const languagesInFiles = (files) => {
    const found = new Set();
    (files || []).forEach((f) =>
        fileLanguageNames(f).forEach((name) => {
            if (name) found.add(String(name).trim().toLowerCase().replace(/-(vo|dubsync)$/, ''));
        }),
    );
    return LANGUAGE_NAMES.filter((n) => found.has(n.toLowerCase()));
};

/**
 * One row per language for a file, sorted by name: { key, label, task }. task is the open task, or
 * undefined. A task's language can be missing from the file, such as a VO track, so tasks add rows.
 */
export const languageTracking = (file, tasks) => {
    const open = new Map((tasks || []).map((t) => [t.languageKey, t]));
    const rows = languagesInFiles([file]).map((label) => {
        const key = languageKey(label);
        const task = open.get(key);
        open.delete(key);
        return { key, label, task };
    });
    open.forEach((task, key) => rows.push({ key, label: task.languageLabel, task }));
    return rows.sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Starts tracking right after files are created ("Track this work" in a create dialog).
 * Never throws: a tracking failure must not look like the upload failed.
 */
export const startTrackingForNewContent = async (contentType, contentIds, fields) => {
    if (!fields?.track || !fields.languages?.length) return;
    const items = contentIds.filter(Boolean).map((contentId) => ({ contentType, contentId }));
    if (items.length === 0) return;
    try {
        const result = await withAccessConfirmation((grant) =>
            TaskTrackingService.startBulk(bulkBody(items, fields, { grantContentAccess: grant })),
        );
        if (!result) return;
        refreshTaskSummary();
        const created = result.items.filter((i) => i.status === 'CREATED').length;
        const failed = result.items.filter((i) => i.status !== 'CREATED');
        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: failed.length ? 'warning' : 'success',
            title: `Tracking ${created} language${created === 1 ? '' : 's'}`,
            text: failed.length ? `${failed.length} not started: ${failed.map((f) => `${f.language} (${f.message || f.status})`).join(', ')}` : undefined,
            showConfirmButton: false,
            timer: 5000,
        });
    } catch (e) {
        Swal.fire({ icon: 'warning', title: 'File created, but tracking did not start', text: taskErrorMessage(e) });
    }
};
